import { createPremiumIcon, icon, node, text, fragment } from "~/util/dom";
import { _ } from "~/route/globals";
import PopOver from "~/ui/popover";
import { createRoutePremiumIconWithTooltip } from "./utils";
import { standardDistanceFormat } from "~/util/format";
import { ROUTE_CONST } from "~/route/const";
import createSDSIcon from "~/ui/icon";
import { ROUTE_AVOID_HIGHWAY, LINK_TO_OUTSIDE } from "~/ui/sds-compatible-icons";

interface ICreateCheckboxTextLine {
	label: string;
	isPremium?: boolean;
	checked?: boolean;
	disabled?: boolean;
	onChange?: (checked: boolean) => void;
}

interface ICreateItemOutput {
	container: HTMLDivElement;
	setChecked: (checked: boolean) => void;
}

function createCheckboxTextLine({
	label,
	isPremium = false,
	checked = false,
	disabled = false,
	onChange = () => {},
}: ICreateCheckboxTextLine): ICreateItemOutput {
	const container = node("div", { className: "route-car-options__checkboxTextLine" });
	const textPart = node("div", { className: "route-car-options__checkboxTextLine__textPart" });
	const textElem = node("span", { className: "route-car-options__checkboxTextLine__title", textContent: label });
	const toggle = node("mapy-toggle", { checked, disabled });

	textPart.append(textElem);
	container.append(textPart);
	container.append(toggle);

	if (isPremium) {
		textPart.append(createRoutePremiumIconWithTooltip());
	}

	if (disabled) {
		container.classList.add("disabled");
	} else {
		textPart.addEventListener("click", () => {
			toggle.checked = !toggle.checked;
			onChange(toggle.checked);
		});
		toggle.addEventListener("change", () => {
			onChange(toggle.checked);
		});
	}

	return {
		container,
		setChecked: (newChecked: boolean) => {
			toggle.checked = newChecked;
		},
	};
}

function getStateSubtitle(checked: boolean, country: ICountryItem) {
	return country.totalLength
		? checked
			? _("route.params.avoidDistance")
			: country.payedLength === 0
				? _("route.params.paidZeroDistance")
				: _("route.params.paidDistance", {
					count: standardDistanceFormat(country.payedLength.toString()).string,
					all: standardDistanceFormat(country.totalLength.toString()).string,
				})
		: _("route.params.notOnRoute");
}

function getStateSubtitleDesc(country) {
	const desc = fragment();

	if (country.roadTollLength || country.specialTollLength) {
		desc.append(node("span", {}, _("route.params.toll")));

		if (country.stickerTollLength && country.stickerTollEshopUrl) {
			desc.append(node("span", {}, "•"));
		}
	}

	if (country.stickerTollLength && country.stickerTollEshopUrl) {
		const link = node("a", { href: country.stickerTollEshopUrl, target: "_balnk" }, _("route.params.vignete"));

		link.append(createSDSIcon({
			symbol: LINK_TO_OUTSIDE,
		}));

		link.addEventListener("click", event => event.stopPropagation());

		desc.append(link);
	}

	return desc;
}

interface ICreateStateLine {
	country: ICountryItem;
	title: string;
	checked?: boolean;
	onChange?: (checked: boolean) => void;
}

function createStateLine({
	country,
	title,
	checked = false,
	onChange = () => {},
}: ICreateStateLine): ICreateItemOutput {
	const iconSrc = `/img/flags/${country.iso3.toLowerCase()}.png`;
	const container = node("div", { className: "route-car-options__stateLine" });
	const leftPart = node("div", { className: "route-car-options__stateLine__leftPart" });
	const iconElem = new Image();
	const textPart = node("div", { className: "route-car-options__stateLine__textPart" });
	const titleElem = node("span", { className: "route-car-options__stateLine__textPart__title", textContent: title });
	const subTitleElem = node("span", { className: "route-car-options__stateLine__textPart__subTitle", innerHTML: getStateSubtitle(checked, country) });
	const subTitleDesc = node("span", { className: "route-car-options__stateLine__textPart__subTitle" });
	const toggle = node("mapy-toggle", { checked });

	iconElem.src = iconSrc;
	textPart.append(titleElem, subTitleElem);

	if (country.paidLength) {
		subTitleDesc.append(getStateSubtitleDesc(country));
		textPart.append(subTitleDesc);
	}

	leftPart.append(iconElem, textPart);
	container.append(leftPart, toggle);

	leftPart.addEventListener("click", () => {
		toggle.checked = !toggle.checked;
		subTitleElem.innerHTML = getStateSubtitle(toggle.checked, country);
		if (country.paidLength && !toggle.checked) {
			textPart.append(subTitleDesc);
		} else {
			subTitleDesc.remove();
		}
		onChange(toggle.checked);
	});
	toggle.addEventListener("change", () => {
		subTitleElem.innerHTML = getStateSubtitle(toggle.checked, country);
		if (country.paidLength && !toggle.checked) {
			textPart.append(subTitleDesc);
		} else {
			subTitleDesc.remove();
		}
		onChange(toggle.checked);
	});

	return {
		container,
		setChecked: (newChecked: boolean) => {
			toggle.checked = newChecked;
			if (country.paidLength && !toggle.checked) {
				textPart.append(subTitleDesc);
			} else {
				subTitleDesc.remove();
			}
			subTitleElem.innerHTML = getStateSubtitle(toggle.checked, country);
		},
	};
}

interface ICountryItem {
	iso3: string;
	payedLength: number;
	totalLength: number;
	name: string;
	paidLength: number;
	roadTollLength: number;
	specialTollLength: number;
	stickerTollEshopUrl: string;
	stickerTollLength: number;
}

interface IState {
	avoidToll: boolean,
	avoidHighways: boolean;
	excludeCountries: Array<string>;
}

export const DEF_CAR_OPTIONS: IState = {
	// vyhnout se placenym usekum
	avoidToll: false,
	// vyhnout se dalnicim - PREMIUM
	avoidHighways: false,
	// staty jdou jako exclude country [string]
	excludeCountries: [],
};

export default class ParamsCarOptions {
	#container: HTMLDivElement;

	#state: IState;

	#savedState: IState;

	#label: HTMLDivElement;

	#popup: PopOver;

	#openState: boolean;

	#toggleButton: HTMLButtonElement;

	#iconClosed: SVGSVGElement | HTMLSpanElement;

	#iconOpen: SVGSVGElement | HTMLSpanElement;

	#labelIcon: HTMLSpanElement;

	#content: HTMLDivElement;

	#dynamicOptionsTop: HTMLDivElement;

	#dynamicOptionsBottom: HTMLDivElement;

	#states: HTMLDivElement;

	#statesItems: Array<ICreateItemOutput>;

	_countryData: Array<ICountryItem>;

	constructor(type: any) {
		this.#container = node("div", { className: type.name });
		this.#state = structuredClone(DEF_CAR_OPTIONS);
		this.#savedState = structuredClone(this.#state);
		this.#openState = false;

		this.#label = node("div", { className: "params-select-label" });
		this.#labelIcon = node("span", { className: "params-select-label__iconHolder" });
		this.#toggleButton = node("button");
		this.#iconClosed = icon("forward");
		this.#iconOpen = icon("dropdown");

		const buildData = this.#build();

		this.#content = buildData.container;
		this.#dynamicOptionsTop = buildData.dynamicOptionsTop;
		this.#dynamicOptionsBottom = buildData.dynamicOptionsBottom;
		this.#states = buildData.states;

		this.#toggleButton.appendChild(this.#iconClosed);
		this.#label.append(
			this.#toggleButton,
			text(_("options")),
			this.#labelIcon,
		);

		this.#popup = new PopOver({
			anchor: this.#label,
			content: this.#content,
			className: "params-select-popup car-options",
			width: 302,
			onClose: () => {
				// nastavime puvodni stav
				this.#state = structuredClone(this.#savedState);
				this.#updateBuild();
				this.#updateLabelIcon();
				this.#close();
			},
			offset: 8,
		});

		this.#label.addEventListener("click", this, false);
		this.#container.append(this.#label);
		this.#statesItems = [];
		this._countryData = [];

		Mapy.CML.addListener((request, state, fromRestore) => {
			if (fromRestore || !state.route.responseData[state.route.index] || request) {
				return;
			}

			this.#updateStateFromRouteData(state.route);
			this.#updateBuild();
		}, "route.request");

		Mapy.CML.addListener((index, state) => {
			if (!state.route.responseData[state.route.index]) {
				return;
			}

			this.#updateStateFromRouteData(state.route);
			this.#updateBuild();
		}, "route.index");

		Mapy.CML.addListener((clear: boolean, state) => {
			if (clear) {
				this.#updateStateFromRouteData(state.route);
				this.#updateBuild();
			}
		}, "route.clear");

		// kvuli zmene usera - premium/free
		Mapy.CML.addListener((active: boolean, state) => {
			if (active) {
				this.#updateStateFromRouteData(state.route);
				this.#updateBuild();
			}
		}, "route.active");

		Mapy.CML.addListener((params, state) => {
			this.#updateStateFromRouteData(state.route);
		}, "route.params");

		this.#updateStateFromRouteData(Mapy.CML.state.route);
		this.#build();
		this.#updateBuild();
	}

	#updateStateFromRouteData(routeData?: any) {
		this._countryData = routeData?.responseData && routeData.responseData[routeData.index]
			? routeData.responseData[routeData.index].countryData
			: [];
		this.#state.excludeCountries = routeData?.params
			? routeData.params.excludeCountries || []
			: [];
		this.#state.avoidHighways = routeData?.params
			? routeData.params.avoidHighways
			: false;
		this.#state.avoidToll = routeData?.params
			? ROUTE_CONST.TYPE_CAR_AVOID_TOLL_CRITERIONS.includes(routeData?.params.criterion)
			: false;

		if (this._countryData.length > 1) {
			this.#state.avoidToll = false;
		} else {
			this.#state.avoidToll = this.#state.avoidToll || this.#state.excludeCountries.length > 0;
		}
	}

	destroy() {
		this.#close();
		this.#toggleButton.removeEventListener("click", this, false);
		this.#container.remove();
		this.#container = null;
	}

	handleEvent(event: Event) {
		const target = event.target as Node;

		if (this.#label.contains(target) || this.#label === target) {
			if (this.#openState) {
				this.#popup.close();
			} else {
				this.#savedState = structuredClone(this.#state);
				this.#open();
			}
		}
	}

	get input() {
		return this.#container;
	}

	get state() {
		return structuredClone(this.#state);
	}

	#build() {
		const container = node("div", { className: "route-car-options" });
		const dynamicOptionsTop = node("div", { className: "route-car-options__dynamicOptions" });
		const dynamicOptionsBottom = node("div", { className: "route-car-options__dynamicOptions" });
		const states = node("div", { className: "route-car-options__states" });
		const title = node("h4", { className: "route-car-options__title", textContent: _("options") });
		const buttons = node("div", { className: "route-car-options__buttons" });
		const doneBtn = node("mapy-router-button", {}, _("done"));

		container.append(title);
		container.append(dynamicOptionsTop);
		container.append(states);
		container.append(dynamicOptionsBottom);
		doneBtn.addEventListener("click", () => {
			this.#change();
			// prepiseme aktualnima zmenama
			this.#savedState = structuredClone(this.#state);
			this.#popup.close();
		});
		buttons.append(doneBtn);
		container.append(buttons);

		return {
			container,
			dynamicOptionsTop,
			dynamicOptionsBottom,
			states,
		};
	}

	#updateBuild() {
		const userIsPremium = Mapy.CML.state.route.userIsPremium;

		this.#dynamicOptionsTop.innerHTML = "";
		this.#dynamicOptionsBottom.innerHTML = "";
		this.#states.innerHTML = "";
		this.#statesItems = [];

		this.#dynamicOptionsBottom.append(createCheckboxTextLine({
			label: _("route.params.carOptions.avoidHighway"),
			isPremium: !userIsPremium,
			checked: this.#state.avoidHighways,
			disabled: !userIsPremium,
			onChange: checked => {
				this.#state.avoidHighways = checked;

				this.#updateLabelIcon();
			},
		}).container);

		let avoidTollData = null;

		if (this._countryData.length > 1) {
			avoidTollData = createCheckboxTextLine({
				label: _("route.params.carOptions.avoidToll"),
				checked: this._countryData.length === this.#state.excludeCountries.length,
				onChange: checked => {
					this.#updateExcludeCountries(checked);

					for (const stateItem of this.#statesItems) {
						stateItem.setChecked(checked);
					}

					this.#updateLabelIcon();
				},
			});
		} else {
			avoidTollData = createCheckboxTextLine({
				label: _("route.params.tollOption"),
				checked: this.#state.avoidToll,
				onChange: checked => {
					this.#state.avoidToll = checked;
					this.#updateExcludeCountries(checked);

					for (const stateItem of this.#statesItems) {
						stateItem.setChecked(checked);
					}

					this.#updateLabelIcon();
				},
			});
		}

		this.#dynamicOptionsTop.append(avoidTollData.container);

		for (const country of this._countryData) {
			const checked = this.#state.excludeCountries.includes(country.iso3);
			const stateData = createStateLine({
				country,
				title: country.name,
				checked,
				onChange: checkedState => {
					const ind = this.#state.excludeCountries.indexOf(country.iso3);

					if (checkedState && ind === -1) {
						this.#state.excludeCountries.push(country.iso3);

						if (this.#state.excludeCountries.length === this._countryData.length) {
							avoidTollData.setChecked(true);
						}
					} else {
						this.#state.excludeCountries.splice(ind, 1);
						avoidTollData.setChecked(false);
					}

					this.#updateLabelIcon();
				},
			});

			this.#states.append(stateData.container);
			this.#statesItems.push(stateData);
		}

		this.#updateLabelIcon();
	}

	#open() {
		this.#iconClosed.remove();
		this.#toggleButton.insertBefore(this.#iconOpen, this.#toggleButton.firstChild);

		this.#popup.open();

		this.#label.classList.add("open");

		this.#openState = true;
	}

	#close() {
		this.#iconOpen.remove();
		this.#toggleButton.insertBefore(this.#iconClosed, this.#toggleButton.firstChild);

		this.#label.classList.remove("open");

		this.#openState = false;
	}

	#change() {
		const event = new CustomEvent("params-car-options-change");

		this.#container.dispatchEvent(event);
	}

	#updateLabelIcon() {
		this.#labelIcon.innerHTML = "";

		if (this.#state.avoidToll || this.#state.excludeCountries.length > 0) {
			this.#labelIcon.append(icon("avoid-pay"));
		}

		const userIsPremium = Mapy.CML.state.route.userIsPremium;

		if (this.#state.avoidHighways) {
			if (userIsPremium) {
				this.#labelIcon.append(createSDSIcon({ symbol: ROUTE_AVOID_HIGHWAY, className: "avoid-highway" }));
			} else {
				this.#labelIcon.append(createPremiumIcon());
			}
		}
	}

	#updateExcludeCountries(isChecked: boolean) {
		this.#state.excludeCountries = isChecked
			? this._countryData.map(item => item.iso3)
			: [];
	}
}
